import { yupResolver } from '@hookform/resolvers/yup';
import {
  Stack,
  Button,
  Dialog,
  MenuItem,
  IconButton,
  DialogTitle,
  DialogActions,
  DialogContent,
  InputAdornment,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import FormProvider, { RHFSelect, RHFSwitch, RHFTextField } from 'src/components/hook-form';
import { enqueueSnackbar } from 'notistack';
import axios, { endpoints } from 'src/utils/axios';
import { toastError } from 'src/utils/format-error';
import * as Yup from 'yup';
import Iconify from 'src/components/iconify';
import { levelOptions } from './utils';

const Schema = Yup.object().shape({
  email: Yup.string().email('รูปแบบอีเมลไม่ถูกต้อง').required('กรุณากรอกอีเมล'),
  password: Yup.string().min(8, 'รหัสผ่านต้องมีอย่างน้อย 8 ตัวอักษร').required('กรุณากรอกรหัสผ่าน'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password')], 'รหัสผ่านไม่ตรงกัน')
    .required('กรุณายืนยันรหัสผ่าน'),
  firstName: Yup.string().required('กรุณากรอกชื่อ'),
  lastName: Yup.string().required('กรุณากรอกนามสกุล'),
  // ปรับ phoneNumber ไม่บังคับกรอก
  phoneNumber: Yup.string()
    .nullable()
    .test('is-valid-phone', 'เบอร์โทรศัพท์ต้องเป็นตัวเลข 10 หลักและขึ้นต้นด้วย 0', (value) => {
      if (!value || value.trim() === '') return true;
      return /^0\d{9}$/.test(value);
    }),
  level: Yup.string().required('จำเป็นต้องระบุระดับผู้ใช้งาน'),
  isPreRegistered: Yup.boolean().notRequired(),
});

type Props = {
  open: boolean;
  onClose: VoidFunction;
  handleReload: VoidFunction;
};

export default function CreateUserDialog({ open, onClose, handleReload }: Props) {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const methods = useForm({
    mode: 'onChange',
    resolver: yupResolver(Schema),
    defaultValues: {
      email: '',
      password: '',
      confirmPassword: '',
      firstName: '',
      lastName: '',
      phoneNumber: '',
      level: 'LV0',
      isPreRegistered: false,
    },
  });

  const {
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = handleSubmit(async (values) => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { confirmPassword, ...payload } = values;

      await axios.post(endpoints.users.addUser, payload);

      enqueueSnackbar('สร้างผู้ใช้สำเร็จ', { variant: 'success' });
      handleReload();
      beforeClose();
    } catch (error) {
      toastError(error);
    }
  });

  const beforeClose = () => {
    reset();
    onClose();
  };

  const formLevelOptions = levelOptions.filter((option) => option.value !== 'all');

  return (
    <Dialog open={open} onClose={beforeClose} maxWidth="sm" fullWidth>
      <FormProvider methods={methods} onSubmit={onSubmit}>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
            <span>สร้างผู้ใช้</span>
            <RHFSwitch
              name="isPreRegistered"
              label="ลงทะเบียนล่วงหน้า"
              labelPlacement="start"
              sx={{ alignItems: 'center', justifyContent: 'center' }}
            />
          </Stack>
        </DialogTitle>

        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <Stack direction="row" spacing={2}>
              <RHFTextField
                name="firstName"
                label="ชื่อ"
                placeholder="ชื่อ"
                InputLabelProps={{ shrink: true }}
              />

              <RHFTextField
                name="lastName"
                label="นามสกุล"
                placeholder="นามสกุล"
                InputLabelProps={{ shrink: true }}
              />
            </Stack>

            <RHFTextField
              name="phoneNumber"
              label="เบอร์ติดต่อ (ไม่บังคับ)"
              placeholder="0XXXXXXXXX"
              InputLabelProps={{ shrink: true }}
            />

            <RHFSelect
              name="level"
              label="ระดับ"
              className="w-1/2"
              InputLabelProps={{ shrink: true }}
              placeholder="เลือกระดับ"
            >
              {formLevelOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </RHFSelect>

            <RHFTextField
              name="email"
              label="อีเมล"
              placeholder="example@gmail.com"
              InputLabelProps={{ shrink: true }}
            />

            <RHFTextField
              name="password"
              label="รหัสผ่าน"
              placeholder="รหัสผ่าน"
              type={showPassword ? 'text' : 'password'}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                      <Iconify icon={showPassword ? 'solar:eye-closed-linear' : 'solar:eye-bold'} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <RHFTextField
              name="confirmPassword"
              label="ยืนยันรหัสผ่าน"
              placeholder="ยืนยันรหัสผ่าน"
              type={showConfirmPassword ? 'text' : 'password'}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      <Iconify
                        icon={showConfirmPassword ? 'solar:eye-closed-linear' : 'solar:eye-bold'}
                      />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" onClick={beforeClose} disabled={isSubmitting}>
            ยกเลิก
          </Button>
          <LoadingButton variant="contained" type="submit" loading={isSubmitting}>
            สร้าง
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}
